import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client'
import type { Order, TradeIdeaDetail as Detail, Transaction } from '../api/client'
import { useHasPermission } from '../api/session'
import { useItemQuery } from '../hooks/useItemQuery'
import { ErrorBox, Loading } from '../components/common/Feedback'
import { IdeeAusklapp } from '../components/ideen/IdeeAusklapp'
import { AblehnenDialog, AbschliessenDialog, AnnehmenDialog } from './TradeIdeaDialogs'
import {
  AusfuehrungDialog,
  OrderDialog,
  StornoDialog,
  TransaktionDialog,
  TransaktionLoeschenDialog,
} from './DepotDialogs'

//: Zwei Schreibrechte: Die Idee annehmen, ablehnen und abschließen darf, wer Ideen
//: schreiben darf. Orders und Transaktionen gehören zum DEPOT und brauchen
//: dessen Recht — die Idee ist eine Absicht, das Depot ist Geld.
const WRITE_PERMISSION = 'trade_idea:write'
const DEPOT_PERMISSION = 'depot:write'

/** Welcher Dialog offen ist. `null` heißt keiner. */
type Dialogwahl = null
  | { art: 'transaktion' }
  | { art: 'order' }
  | { art: 'annehmen' }
  | { art: 'ablehnen' }
  | { art: 'ende' }
  | { art: 'loeschen'; transaktion: Transaction }
  | { art: 'ausfuehren'; order: Order }
  | { art: 'storno'; order: Order }

/**
 * Was hinter einer Handelsidee steckt — geladen und mit den Dialogen.
 *
 * Wird beim Aufklappen NACHGELADEN. Die Liste liefert die abhängigen Zeilen
 * nicht mit, und das ist richtig so: Bei fünfzig Vorschlägen wären es
 * fünfzig Bäume, von denen man einen ansieht. Wie es aussieht, steht in
 * `IdeeAusklapp`; hier stehen Laden, Rechte und die Dialoge.
 */
export function TradeIdeaDetail({ id, currency, stand = 0, onGeaendert, onGeladen }: {
  id: number
  /** Zielwährung, wie sie oben in der Liste gewählt wurde. Leer heißt: keine. */
  currency?: string
  /**
   * Ein Zähler der Seite: Sie zählt hoch, wenn sie SELBST an der Idee
   * geschrieben hat — über die Zeichen an der Zeile —, und das Detail lädt
   * neu. Sonst stünde oben „angenommen" und hier noch „vorgeschlagen".
   */
  stand?: number
  /**
   * Nach einem Schreiben — Buchung, Order, Löschen, Annehmen, Ablehnen, Abschließen.
   * Die Zeile darüber zeigt Status und Abstand und wüsste sonst nichts davon.
   */
  onGeaendert?: () => void
  /** Sobald das Detail da ist — die Zeile braucht die Ziele für ihre Leiter. */
  onGeladen?: (detail: Detail) => void
}) {
  const darfSchreiben = useHasPermission(WRITE_PERMISSION)
  const darfBuchen = useHasPermission(DEPOT_PERMISSION)
  const [dialog, setDialog] = useState<Dialogwahl>(null)
  // Ein Zähler als Auslöser zum Neuladen: Nach einer Buchung ist die Idee
  // eine andere — Status, Position, Transaktionen —, und die Ladefunktion
  // soll erneut laufen, obwohl sich Kennung und Währung nicht geändert haben.
  const [tick, setTick] = useState(0)

  // `tick` und `stand` stehen in den Abhängigkeiten, ohne im Rumpf
  // vorzukommen: Genau das ist ihr Zweck — eine NEUE Funktion, damit der
  // Effekt erneut lädt.
  const laden = useCallback(
    (signal: AbortSignal) => api.tradeIdea(
      id, currency ? { currency: [currency] } : {}, signal),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, currency, tick, stand])
  const { item, loading, error } = useItemQuery(laden)

  // Nur `item`, nicht der Rückruf: Der wird bei jedem Rendern der Seite neu
  // gebildet, und der Effekt liefe sonst endlos.
  useEffect(() => { if (item) onGeladen?.(item) }, [item])  // eslint-disable-line react-hooks/exhaustive-deps

  function fertig() {
    setDialog(null)
    setTick((n) => n + 1)
    onGeaendert?.()
  }

  if (loading && !item) return <Loading>lädt die Idee …</Loading>
  if (error) return <ErrorBox>{error}</ErrorBox>
  if (!item) return null

  return (
    <>
      <IdeeAusklapp idee={item} darfSchreiben={darfSchreiben} darfBuchen={darfBuchen}
                    onOrder={() => setDialog({ art: 'order' })}
                    onTransaktion={() => setDialog({ art: 'transaktion' })}
                    onAnnehmen={() => setDialog({ art: 'annehmen' })}
                    onAblehnen={() => setDialog({ art: 'ablehnen' })}
                    onAbschliessen={() => setDialog({ art: 'ende' })}
                    onLoeschen={(transaktion) => setDialog({ art: 'loeschen', transaktion })}
                    onAusfuehren={(order) => setDialog({ art: 'ausfuehren', order })}
                    onStorno={(order) => setDialog({ art: 'storno', order })} />

      {/*
        Bedingt gerendert statt mit `offen`-Eigenschaft: So beginnt jeder
        Dialog leer, ohne dass jemand zurücksetzen müsste.
      */}
      {dialog?.art === 'transaktion' && (
        <TransaktionDialog idee={item} onFertig={fertig} onAbbrechen={() => setDialog(null)} />
      )}
      {dialog?.art === 'order' && (
        <OrderDialog idee={item} onFertig={fertig} onAbbrechen={() => setDialog(null)} />
      )}
      {dialog?.art === 'annehmen' && (
        <AnnehmenDialog idee={item} currency={currency} onFertig={fertig}
                        onAbbrechen={() => setDialog(null)} />
      )}
      {dialog?.art === 'ablehnen' && (
        <AblehnenDialog idee={item} currency={currency} onFertig={fertig}
                        onAbbrechen={() => setDialog(null)} />
      )}
      {dialog?.art === 'ende' && (
        <AbschliessenDialog idee={item} currency={currency} onFertig={fertig}
                            onAbbrechen={() => setDialog(null)} />
      )}
      {dialog?.art === 'loeschen' && (
        <TransaktionLoeschenDialog transaktion={dialog.transaktion} onFertig={fertig}
                                   onAbbrechen={() => setDialog(null)} />
      )}
      {dialog?.art === 'ausfuehren' && (
        <AusfuehrungDialog order={dialog.order} onFertig={fertig} onAbbrechen={() => setDialog(null)} />
      )}
      {dialog?.art === 'storno' && (
        <StornoDialog order={dialog.order} onFertig={fertig} onAbbrechen={() => setDialog(null)} />
      )}
    </>
  )
}
